import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  StaffSessionError,
  clearStaffToken,
  fetchStaffMe,
  getStaffToken,
  setStaffToken,
  staffLogin,
} from '../services/staffApi';

const StaffAuthContext = createContext();

export const useStaffAuth = () => {
  const context = useContext(StaffAuthContext);
  if (!context) {
    throw new Error('useStaffAuth debe ser usado dentro de StaffAuthProvider');
  }
  return context;
};

// Solo el token se guarda (`staff-token`). El staff (rol y actividades) y la
// empresa se piden a GET /api/auth/staff/me en cada carga: los permisos se
// consultan en BD y no se leen del claim del token ni de una copia local.
export const StaffAuthProvider = ({ children }) => {
  const [staff, setStaff] = useState(null);
  const [empresa, setEmpresa] = useState(null);
  // true mientras hay token y todavía no sabemos si sigue siendo válido
  const [loading, setLoading] = useState(() => Boolean(getStaffToken()));
  // motivo por el que se perdió la sesión, para mostrarlo en el login
  const [sessionNotice, setSessionNotice] = useState('');
  // hay token pero no se pudo consultar /me (sin conexión): no se sabe si sigue vigente
  const [connectionError, setConnectionError] = useState('');
  const mounted = useRef(true);

  // StrictMode (desarrollo) monta, desmonta y vuelve a montar: hay que volver a marcarlo como montado
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const endSession = useCallback((notice = '') => {
    clearStaffToken();
    setStaff(null);
    setEmpresa(null);
    setSessionNotice(notice);
    setConnectionError('');
  }, []);

  // Refresca rol y actividades desde la BD. Si la sesión ya no es válida
  // (code staff_session_invalid) borra el token y devuelve false.
  const refresh = useCallback(async () => {
    if (!getStaffToken()) {
      endSession();
      return false;
    }
    try {
      const data = await fetchStaffMe();
      if (!mounted.current) return true;
      setStaff(data.staff);
      setEmpresa(data.empresa);
      setConnectionError('');
      return true;
    } catch (error) {
      if (error instanceof StaffSessionError) {
        if (mounted.current) endSession(error.message);
        return false;
      }
      // Fallo de red u otro: se conserva lo que ya se sabía.
      if (mounted.current) setConnectionError(error.message);
      throw error;
    }
  }, [endSession]);

  useEffect(() => {
    if (!getStaffToken()) {
      setLoading(false);
      return;
    }
    refresh()
      .catch(() => {
        // sin conexión: queda `connectionError` y las pantallas ofrecen reintentar
      })
      .finally(() => { if (mounted.current) setLoading(false); });
  }, [refresh]);

  const login = async (email, password) => {
    try {
      const data = await staffLogin(email, password);
      setStaffToken(data.token);
      setStaff(data.staff);
      setEmpresa(data.empresa);
      setSessionNotice('');
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message, code: error.code };
    }
  };

  const logout = () => {
    // Los escaneos recientes guardan nombres de clientes: no se dejan en un
    // dispositivo compartido al cerrar sesión.
    try {
      if (staff) localStorage.removeItem(`recentScans-staff-${staff.id}`);
    } catch (e) {
      // sin localStorage no hay nada que borrar
    }
    endSession();
  };

  const value = {
    staff,
    empresa,
    loading,
    sessionNotice,
    connectionError,
    login,
    logout,
    refresh,
    endSession,
    isAuthenticated: Boolean(staff),
  };

  return (
    <StaffAuthContext.Provider value={value}>
      {children}
    </StaffAuthContext.Provider>
  );
};
